import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { ICarrierAccount } from "@redotech/redo-model/carrier-account/carrier-account";
import { createContext, memo, ReactNode, useMemo } from "react";

export interface CarrierAccountsContextValue {
  merchantCarrierAccounts: ICarrierAccount[];
  redoChildCarrierAccounts: ICarrierAccount[];
  getCarrierAccountName: (carrierAccountId: string) => string | undefined;
}

export const CarrierAccountsContext =
  createContext<CarrierAccountsContextValue | null>(null);

export const CarrierAccountsProvider = memo(function CarrierAccountsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const carrierAccountsLoad = useLoad(
    async () => rpcClient.getCarrierAccounts(null),
    [rpcClient],
  );

  const value = useMemo<CarrierAccountsContextValue | null>(() => {
    if (!carrierAccountsLoad.value) {
      return null;
    }

    const { merchantCarrierAccounts, redoChildCarrierAccounts } =
      carrierAccountsLoad.value;

    // Build a map of carrier account ID to name
    const carrierAccountIdToName = new Map<string, string>();
    for (const account of [
      ...merchantCarrierAccounts,
      ...redoChildCarrierAccounts,
    ]) {
      // Use carrierAccountId for easypost accounts, _id for other accounts
      const accountId = account.carrierAccountId ?? account._id.toString();
      carrierAccountIdToName.set(accountId, account.name);
    }

    return {
      merchantCarrierAccounts,
      redoChildCarrierAccounts,
      getCarrierAccountName: (carrierAccountId: string) =>
        carrierAccountIdToName.get(carrierAccountId),
    };
  }, [carrierAccountsLoad.value]);

  return (
    <CarrierAccountsContext.Provider value={value}>
      {children}
    </CarrierAccountsContext.Provider>
  );
});
